import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';
import NotificationsButton from './NotificationsButton';
import ThemeToggle from './ThemeToggle';
import { AppBarBack, useAppBarBack } from './shell/AppBarContext';

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Status shown next to the title (above it on mobile), e.g. a StatusPill. */
  badge?: React.ReactNode;
  /** Parent page for detail screens. On mobile it replaces the logo in the app bar. */
  back?: AppBarBack;
  /** Page-specific actions shown after the theme toggle and bell from tablet up. */
  actions?: React.ReactNode;
  /** Actions shown under the titles on mobile (e.g. "File a claim" + "WhatsApp"). */
  mobileActions?: React.ReactNode;
  className?: string;
}

/**
 * Top of every dashboard page. From tablet up it also holds the theme toggle and
 * notifications bell; on mobile those live in the app bar, so only the titles show.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, badge, back, actions, mobileActions, className }) => {
  useAppBarBack(back);
  return (
    <header className={cn('tw-flex tw-flex-col tw-gap-4', className)}>
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
          {back && (
            <Link
              to={back.to}
              className="tw-mb-1 tw-hidden tw-items-center tw-gap-1 tw-self-start tw-rounded tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-ds-text-secondary hover:tw-text-ds-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand md:tw-flex"
            >
              <Icon icon={ChevronLeft} size={16} />
              {back.label}
            </Link>
          )}
          {badge && <div className="tw-mb-1 md:tw-hidden">{badge}</div>}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2">
            <h1 className="tw-text-[26px] tw-font-bold tw-leading-8 tw-tracking-[-0.26px] tw-text-ds-text-primary lg:tw-text-[30px] lg:tw-leading-[38px] lg:tw-tracking-[-0.3px]">
              {title}
            </h1>
            {badge && <div className="tw-hidden md:tw-block">{badge}</div>}
          </div>
          {subtitle && (
            <p className="tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary md:tw-text-[15px] md:tw-leading-[22px]">{subtitle}</p>
          )}
        </div>
        <div className="tw-hidden tw-shrink-0 tw-items-center tw-gap-2.5 md:tw-flex">
          <ThemeToggle />
          <NotificationsButton />
          {actions}
        </div>
      </div>
      {mobileActions && <div className="tw-flex tw-gap-2.5 md:tw-hidden [&>*]:tw-flex-1">{mobileActions}</div>}
    </header>
  );
};

export default PageHeader;
